import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  const navigate = useNavigate();
  const { login, user, authLoaded } = useAuth();
  const { clearCart } = useCart();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const result = await login(email, password);

      if (!result.success) {
        setError(result.message || 'Invalid credentials');
        return;
      }

      if (result.user?.isAdmin) {
        clearCart();
        navigate('/admin/main', { replace: true });
        return;
      }

      navigate('/dashboard', { replace: true });
    } catch {
      setError('Login failed. Please try again.');
    }
  };

  if (authLoaded && user?.isAdmin) return <Navigate to="/admin/main" replace />;
  if (authLoaded && user) return <Navigate to="/dashboard" replace />;

  return (
    <div style={styles.page}>

      {/* BACKGROUND BLOBS */}
      <div style={styles.blob1} />
      <div style={styles.blob2} />
      <div style={styles.blob3} />

      {/* LEFT PANEL (DESKTOP ONLY) */}
      {!isMobile && (
        <div style={styles.leftPanel}>
          <div style={styles.overlay} />
          <div style={styles.hero}>
            <h1 style={styles.heroTitle}>Welcome<br />Back</h1>
            <p style={styles.heroText}>
              Secure access to your dashboard, analytics & automation tools.
            </p>
          </div>
        </div>
      )}

      {/* RIGHT PANEL */}
      <div style={styles.rightPanel}>

        <div style={{
          ...styles.card,
          ...(isMobile ? { margin: '0 auto', width: '92%' } : {})
        }}>

          <h1 style={styles.title}>Sign In</h1>
          <p style={styles.subtitle}>Welcome back to your account</p>

          {error && <div style={styles.error}>{error}</div>}

          <form onSubmit={handleLogin} style={styles.form}>

            <input
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              required
            />

            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              required
            />

            <div style={styles.forgotRow}>
              <Link to="/forgot-password" style={styles.link}>
                Forgot password?
              </Link>
            </div>

            <button type="submit" style={styles.button}>
              Sign In
            </button>

          </form>

          <div style={styles.bottom}>
            New here?{' '}
            <Link to="/register" style={styles.linkStrong}>
              Create account
            </Link>
          </div>

        </div>

      </div>
    </div>
  );
}

export default Login;

/* ================= STYLES ================= */

const styles = {

  page: {
    minHeight: '100vh',
    display: 'flex',
    background: '#020617',
    fontFamily: 'Inter, sans-serif',
    position: 'relative',
    overflow: 'hidden'
  },

  /* BACKGROUND BLOBS */
  blob1: {
    position: 'absolute',
    width: 650,
    height: 650,
    top: -250,
    left: -250,
    borderRadius: '50%',
    background: 'radial-gradient(circle, #6366f1 0%, transparent 60%)',
    filter: 'blur(120px)',
    opacity: 0.45
  },

  blob2: {
    position: 'absolute',
    width: 650,
    height: 650,
    bottom: -280,
    right: -250,
    borderRadius: '50%',
    background: 'radial-gradient(circle, #0ea5e9 0%, transparent 60%)',
    filter: 'blur(140px)',
    opacity: 0.35
  },

  blob3: {
    position: 'absolute',
    width: 500,
    height: 500,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '50%',
    background: 'radial-gradient(circle, #8b5cf6 0%, transparent 65%)',
    filter: 'blur(160px)',
    opacity: 0.18
  },

  /* LEFT PANEL */
  leftPanel: {
    flex: 1,
    position: 'relative',
    background:
      'url(https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=1800&q=80)',
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  },

  overlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, rgba(2,6,23,.95), rgba(15,23,42,.7))'
  },

  hero: {
    position: 'relative',
    zIndex: 2,
    padding: 80,
    color: '#fff',
    maxWidth: 520
  },

  heroTitle: {
    fontSize: 60,
    fontWeight: 900,
    lineHeight: 1.1
  },

  heroText: {
    marginTop: 16,
    color: '#cbd5e1'
  },

  /* RIGHT PANEL */
  rightPanel: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    position: 'relative',
    zIndex: 10
  },

  /* CARD */
  card: {
    width: '100%',
    maxWidth: 460,
    padding: 42,
    borderRadius: 32,
    background: 'rgba(255,255,255,.08)',
    border: '1px solid rgba(255,255,255,.12)',
    backdropFilter: 'blur(40px)',
    WebkitBackdropFilter: 'blur(40px)',
    boxShadow: '0 40px 120px rgba(0,0,0,.6)',
    color: '#fff'
  },

  title: {
    fontSize: 34,
    fontWeight: 900
  },

  subtitle: {
    color: '#94a3b8',
    marginTop: 6,
    marginBottom: 20
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },

  input: {
    height: 56,
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,.12)',
    background: 'rgba(255,255,255,.05)',
    color: '#fff',
    padding: '0 16px',
    fontSize: 15,
    outline: 'none'
  },

  forgotRow: {
    display: 'flex',
    justifyContent: 'flex-end'
  },

  link: {
    color: '#60a5fa',
    fontSize: 14,
    textDecoration: 'none'
  },

  button: {
    height: 58,
    borderRadius: 18,
    border: 'none',
    fontWeight: 800,
    color: '#fff',
    background: 'linear-gradient(135deg,#6366f1,#3b82f6,#06b6d4)'
  },

  bottom: {
    marginTop: 18,
    textAlign: 'center',
    color: '#94a3b8'
  },

  linkStrong: {
    color: '#60a5fa',
    fontWeight: 700,
    textDecoration: 'none'
  },

  error: {
    marginBottom: 14,
    padding: 12,
    borderRadius: 12,
    background: 'rgba(239,68,68,.15)',
    border: '1px solid rgba(239,68,68,.3)',
    color: '#fecaca'
  }
};